import {
    Component,
    Input,
    Output,
    OnInit,
    AfterContentInit,
    OnDestroy,
    OnChanges,
    ElementRef,
    EventEmitter,
    SimpleChanges
} from '@angular/core';
import { isEqual } from 'lodash';
import { debounceTime, fromEvent, Subject, Subscription } from 'rxjs';
import { MinMax } from '../../core-data-model';
import { BehavioralFactor } from '../../core-data-model/BehavioralFactor.model';
import { BehavioralFactorRanges } from '../../core-data-model/BehavioralFactorRanges.model';
import { Pattern } from '../../core-data-model/Pattern.model';
import {
    DragStartEndMap,
    config
} from '../../core-ui/BehavioralRangesRectangles/BehavioralRanges.interface';
import { BehavioralRangesUtil } from '../../core-ui/BehavioralRangesRectangles/BehavioralRanges.util';
import { Util } from '../../core-utilities/Util';
import { TranslationService } from '../../translations';

@Component({
    selector: 'behavioral-ranges',
    styles: [
        '.not-selectable { -webkit-touch-callout: none; -webkit-user-select: none; -khtml-user-select: none; -moz-user-select: none; -ms-user-select: none; user-select: none; }'
    ],
    template: `
        <div
            class="behavioralRanges scalingSvgContainer"
            piTesting="behavioral-ranges-component"
            [ngClass]="{
                'behavioralRanges--showHelpText': !this.hasBeenHovered
            }"
            [ngStyle]="{
                'padding-bottom': (viewBox.height / viewBox.width) * 100 + '%'
            }"
        >
            <svg [attr.viewBox]="getViewboxDimensions()" class="scalingSvg">
                <svg:defs>
                    <svg:linearGradient
                        id="linearGradient"
                        y1="217.54"
                        [attr.x2]="viewBox.width"
                        y2="217.54"
                        gradientUnits="userSpaceOnUse"
                    >
                        @for (
                            color of config.gradientColors;
                            track color;
                            let i = $index
                        ) {
                            <svg:stop
                                [attr.offset]="i"
                                [attr.stop-color]="color"
                            />
                        }
                    </svg:linearGradient>
                </svg:defs>
                <svg:g id="Ranges">
                    <svg:g>
                        <svg:line
                            x1="914.02"
                            y1="25.11"
                            x2="914.02"
                            y2="204.52"
                            [ngStyle]="config.sigmaLineStyle"
                        />
                        <svg:line
                            x1="731.33"
                            y1="25.11"
                            x2="731.33"
                            y2="204.52"
                            [ngStyle]="config.sigmaLineStyle"
                        />
                        <svg:line
                            x1="548.83"
                            y1="25.11"
                            x2="548.83"
                            y2="204.52"
                            [ngStyle]="config.sigmaLineStyle"
                        />
                        <svg:line
                            x1="365.93"
                            y1="25.11"
                            x2="365.93"
                            y2="204.52"
                            [ngStyle]="config.sigmaLineStyle"
                        />
                        <svg:line
                            x1="183.24"
                            y1="25.11"
                            x2="183.24"
                            y2="204.52"
                            [ngStyle]="config.sigmaLineStyle"
                        />
                        <svg:g id="Bottom_Axis" transform="translate(0, 209)">
                            <svg:rect
                                id="Bottom_Bar"
                                data-name="Bottom Bar"
                                x="0"
                                y="0"
                                [attr.width]="viewBox.width"
                                height="16"
                                [attr.fill]="
                                    SVG.getGradientUrl('linearGradient')
                                "
                            />
                            <!-- midpoint triangle -->
                            <svg:polygon
                                points="549 0 539 16 559 16"
                                style="fill:#231f20"
                            />
                            <svg:text
                                x="10"
                                y="13"
                                fill="white"
                                text-anchor="start"
                                font-size="14.5"
                            >
                                {{
                                    getTranslationValue(
                                        'text.common-terms.low'
                                    ).toUpperCase()
                                }}
                            </svg:text>
                            <svg:text
                                [attr.x]="viewBox.width - 10"
                                y="13"
                                fill="white"
                                text-anchor="end"
                                font-size="14.5"
                            >
                                {{
                                    getTranslationValue(
                                        'text.common-terms.high'
                                    ).toUpperCase()
                                }}
                            </svg:text>
                        </svg:g>
                        <svg:g id="Tick_Marks" data-name="Tick Marks">
                            <svg:line
                                x1="366.26"
                                y1="208.98"
                                x2="366.26"
                                y2="225.96"
                                [ngStyle]="config.tickMarkStyle"
                            />
                            <svg:line
                                x1="220.1"
                                y1="208.98"
                                x2="220.1"
                                y2="216.03"
                                [ngStyle]="config.tickMarkStyle"
                            />
                            <svg:line
                                x1="256.64"
                                y1="208.98"
                                x2="256.64"
                                y2="216.03"
                                [ngStyle]="config.tickMarkStyle"
                            />
                            <svg:line
                                x1="293.18"
                                y1="208.98"
                                x2="293.18"
                                y2="216.03"
                                [ngStyle]="config.tickMarkStyle"
                            />
                            <svg:line
                                x1="329.72"
                                y1="208.98"
                                x2="329.72"
                                y2="216.03"
                                [ngStyle]="config.tickMarkStyle"
                            />
                            <svg:line
                                x1="402.8"
                                y1="208.98"
                                x2="402.8"
                                y2="216.03"
                                [ngStyle]="config.tickMarkStyle"
                            />
                            <svg:line
                                x1="439.34"
                                y1="208.98"
                                x2="439.34"
                                y2="216.03"
                                [ngStyle]="config.tickMarkStyle"
                            />
                            <svg:line
                                x1="475.88"
                                y1="208.98"
                                x2="475.88"
                                y2="216.03"
                                [ngStyle]="config.tickMarkStyle"
                            />
                            <svg:line
                                x1="512.42"
                                y1="208.98"
                                x2="512.42"
                                y2="216.03"
                                [ngStyle]="config.tickMarkStyle"
                            />
                            <svg:line
                                x1="585.5"
                                y1="208.98"
                                x2="585.5"
                                y2="216.03"
                                [ngStyle]="config.tickMarkStyle"
                            />
                            <svg:line
                                x1="622.04"
                                y1="208.98"
                                x2="622.04"
                                y2="216.03"
                                [ngStyle]="config.tickMarkStyle"
                            />
                            <svg:line
                                x1="658.58"
                                y1="208.98"
                                x2="658.58"
                                y2="216.03"
                                [ngStyle]="config.tickMarkStyle"
                            />
                            <svg:line
                                x1="695.12"
                                y1="208.98"
                                x2="695.12"
                                y2="216.03"
                                [ngStyle]="config.tickMarkStyle"
                            />
                            <svg:line
                                x1="768.2"
                                y1="208.98"
                                x2="768.2"
                                y2="216.03"
                                [ngStyle]="config.tickMarkStyle"
                            />
                            <svg:line
                                x1="804.74"
                                y1="208.98"
                                x2="804.74"
                                y2="216.03"
                                [ngStyle]="config.tickMarkStyle"
                            />
                            <svg:line
                                x1="841.28"
                                y1="208.98"
                                x2="841.28"
                                y2="216.03"
                                [ngStyle]="config.tickMarkStyle"
                            />
                            <svg:line
                                x1="877.82"
                                y1="208.98"
                                x2="877.82"
                                y2="216.03"
                                [ngStyle]="config.tickMarkStyle"
                            />
                            <svg:line
                                x1="950.9"
                                y1="208.98"
                                x2="950.9"
                                y2="216.03"
                                [ngStyle]="config.tickMarkStyle"
                            />
                            <svg:line
                                x1="987.44"
                                y1="208.98"
                                x2="987.44"
                                y2="216.03"
                                [ngStyle]="config.tickMarkStyle"
                            />
                            <svg:line
                                x1="1023.98"
                                y1="208.98"
                                x2="1023.98"
                                y2="216.03"
                                [ngStyle]="config.tickMarkStyle"
                            />
                            <svg:line
                                x1="73.94"
                                y1="208.98"
                                x2="73.94"
                                y2="216.03"
                                [ngStyle]="config.tickMarkStyle"
                            />
                            <svg:line
                                x1="110.48"
                                y1="208.98"
                                x2="110.48"
                                y2="216.03"
                                [ngStyle]="config.tickMarkStyle"
                            />
                            <svg:line
                                x1="147.02"
                                y1="208.98"
                                x2="147.02"
                                y2="216.03"
                                [ngStyle]="config.tickMarkStyle"
                            />
                            <svg:line
                                x1="731.66"
                                y1="208.98"
                                x2="731.66"
                                y2="225.96"
                                [ngStyle]="config.tickMarkStyle"
                            />
                            <svg:line
                                x1="183.56"
                                y1="208.98"
                                x2="183.56"
                                y2="225.96"
                                [ngStyle]="config.tickMarkStyle"
                            />
                            <svg:line
                                x1="914.36"
                                y1="208.98"
                                x2="914.36"
                                y2="225.96"
                                [ngStyle]="config.tickMarkStyle"
                            />
                        </svg:g>

                        <svg:rect
                            id="Top_Bar"
                            data-name="Top Bar"
                            y="16.97"
                            [attr.width]="viewBox.width"
                            height="3.68"
                            [ngStyle]="config.inactiveLabel"
                        />

                        <svg:g id="TextLabels" class="not-selectable">
                            @for (factor of factors; track factor) {
                                <svg:g>
                                    <svg:text
                                        x="0"
                                        [attr.y]="factor.height"
                                        text-anchor="start"
                                        font-size="18"
                                        [ngStyle]="
                                            shouldHighlightFactor(
                                                factor.key,
                                                false
                                            )
                                                ? config.activeLabel
                                                : config.inactiveLabel
                                        "
                                    >
                                        {{ getFactorLabel(factor.left) }}
                                    </svg:text>
                                    <svg:text
                                        [attr.x]="viewBox.width"
                                        [attr.y]="factor.height"
                                        text-anchor="end"
                                        font-size="18"
                                        [ngStyle]="
                                            shouldHighlightFactor(
                                                factor.key,
                                                true
                                            )
                                                ? config.activeLabel
                                                : config.inactiveLabel
                                        "
                                    >
                                        {{ getFactorLabel(factor.right) }}
                                    </svg:text>
                                </svg:g>
                            }
                        </svg:g>

                        <svg:g id="TopLabels">
                            <svg:path
                                d="M4.13,7.34H.29V6.15H4.13Z"
                                [ngStyle]="config.inactiveLabel"
                            />
                            <svg:path
                                d="M7.51,5.18H8.61a2.4,2.4,0,0,0,1.62-.54,1.81,1.81,0,0,0,.59-1.42,1.78,1.78,0,0,0-2-2,2.09,2.09,0,0,0-1.51.54,1.89,1.89,0,0,0-.56,1.43H5.29A2.92,2.92,0,0,1,6.28.9,3.62,3.62,0,0,1,8.81,0a3.58,3.58,0,0,1,2.54.86,3.1,3.1,0,0,1,.92,2.38,2.51,2.51,0,0,1-.48,1.45,2.92,2.92,0,0,1-1.32,1,2.81,2.81,0,0,1,1.46,1,2.75,2.75,0,0,1,.52,1.69,3.13,3.13,0,0,1-1,2.45,3.77,3.77,0,0,1-2.62.9,3.87,3.87,0,0,1-2.62-.87,2.9,2.9,0,0,1-1-2.3H6.65A1.87,1.87,0,0,0,7.24,10a2.24,2.24,0,0,0,1.58.54A2.2,2.2,0,0,0,10.43,10,2.12,2.12,0,0,0,11,8.45a1.93,1.93,0,0,0-.61-1.53,2.67,2.67,0,0,0-1.77-.55H7.51Z"
                                [ngStyle]="config.inactiveLabel"
                            />
                            <svg:path
                                d="M22.52,4.3H20.28a3.87,3.87,0,0,1,1.57,3.31v.13a4.33,4.33,0,0,1-.48,2,3.65,3.65,0,0,1-3.32,2,3.49,3.49,0,0,1-2.76-1.19,4.68,4.68,0,0,1-1-3.2v-.1a4.83,4.83,0,0,1,.46-2.15A3.54,3.54,0,0,1,16,3.64a3.66,3.66,0,0,1,2-.54h4.53Zm-6.8,3.15a3.74,3.74,0,0,0,.63,2.29,2.16,2.16,0,0,0,3.43,0,4.1,4.1,0,0,0,.63-2.45,3.42,3.42,0,0,0-.63-2.16A2.07,2.07,0,0,0,18,4.3a2,2,0,0,0-1.7.8A3.76,3.76,0,0,0,15.71,7.45Z"
                                [ngStyle]="config.inactiveLabel"
                            />
                            <svg:path
                                d="M177.49,7.34h-3.84V6.15h3.84Z"
                                [ngStyle]="config.inactiveLabel"
                            />
                            <svg:path
                                d="M186.26,11.61h-7.5v-1l4-4.4a9.45,9.45,0,0,0,1.22-1.62,2.71,2.71,0,0,0,.33-1.29,2.05,2.05,0,0,0-.54-1.47,1.9,1.9,0,0,0-1.45-.57,2.25,2.25,0,0,0-1.69.62,2.36,2.36,0,0,0-.6,1.72h-1.46a3.38,3.38,0,0,1,1-2.56,3.78,3.78,0,0,1,2.73-1,3.63,3.63,0,0,1,2.53.84,2.86,2.86,0,0,1,.93,2.23q0,1.69-2.16,4l-3.07,3.33h5.75Z"
                                [ngStyle]="config.inactiveLabel"
                            />
                            <svg:path
                                d="M195.87,4.3h-2.23a3.87,3.87,0,0,1,1.57,3.31v.13a4.33,4.33,0,0,1-.48,2,3.65,3.65,0,0,1-3.32,2,3.49,3.49,0,0,1-2.76-1.19,4.68,4.68,0,0,1-1-3.2v-.1a4.83,4.83,0,0,1,.46-2.15,3.54,3.54,0,0,1,1.32-1.49,3.66,3.66,0,0,1,2-.54h4.53Zm-6.8,3.15a3.74,3.74,0,0,0,.63,2.29,2.16,2.16,0,0,0,3.43,0,4.1,4.1,0,0,0,.63-2.45,3.42,3.42,0,0,0-.63-2.16,2.07,2.07,0,0,0-1.73-.81,2,2,0,0,0-1.7.8A3.76,3.76,0,0,0,189.07,7.45Z"
                                [ngStyle]="config.inactiveLabel"
                            />
                            <svg:path
                                d="M358.16,7.34h-3.84V6.15h3.84Z"
                                [ngStyle]="config.inactiveLabel"
                            />
                            <svg:path
                                d="M364.21,11.61h-1.46V1.91L359.81,3V1.67L364,.1h.23Z"
                                [ngStyle]="config.inactiveLabel"
                            />
                            <svg:path
                                d="M376.54,4.3h-2.23a3.87,3.87,0,0,1,1.57,3.31v.13a4.33,4.33,0,0,1-.48,2,3.65,3.65,0,0,1-3.32,2,3.49,3.49,0,0,1-2.76-1.19,4.68,4.68,0,0,1-1-3.2v-.1a4.83,4.83,0,0,1,.46-2.15,3.54,3.54,0,0,1,1.32-1.49,3.66,3.66,0,0,1,2-.54h4.53Zm-6.8,3.15a3.74,3.74,0,0,0,.63,2.29,2.16,2.16,0,0,0,3.43,0,4.1,4.1,0,0,0,.63-2.45,3.42,3.42,0,0,0-.63-2.16,2.07,2.07,0,0,0-1.73-.81,2,2,0,0,0-1.7.8A3.76,3.76,0,0,0,369.74,7.45Z"
                                [ngStyle]="config.inactiveLabel"
                            />
                            <svg:path
                                d="M552.35,6.73a6.66,6.66,0,0,1-.87,3.8,3.63,3.63,0,0,1-5.44,0,6.31,6.31,0,0,1-.91-3.63V5A6.51,6.51,0,0,1,546,1.23a3.69,3.69,0,0,1,5.46,0,6.28,6.28,0,0,1,.9,3.65Zm-1.46-2A5.33,5.33,0,0,0,550.38,2a1.79,1.79,0,0,0-1.64-.85A1.76,1.76,0,0,0,547.1,2a5.21,5.21,0,0,0-.53,2.59V7a5.42,5.42,0,0,0,.54,2.74,1.78,1.78,0,0,0,1.63.89,1.76,1.76,0,0,0,1.6-.83,5.22,5.22,0,0,0,.55-2.63Z"
                                [ngStyle]="config.inactiveLabel"
                            />
                            <svg:path
                                d="M724.31,5.47h3.19V6.84h-3.19v3.62h-1.46V6.84h-3.19V5.47h3.19V2.12h1.46Z"
                                [ngStyle]="config.inactiveLabel"
                            />
                            <svg:path
                                d="M733.91,11.61h-1.46V1.91L729.51,3V1.67L733.68.1h.23Z"
                                [ngStyle]="config.inactiveLabel"
                            />
                            <svg:path
                                d="M746.24,4.3H744a3.87,3.87,0,0,1,1.57,3.31v.13a4.33,4.33,0,0,1-.48,2,3.65,3.65,0,0,1-3.32,2A3.49,3.49,0,0,1,739,10.58a4.68,4.68,0,0,1-1-3.2v-.1a4.83,4.83,0,0,1,.46-2.15,3.54,3.54,0,0,1,1.32-1.49,3.66,3.66,0,0,1,2-.54h4.53Zm-6.8,3.15a3.74,3.74,0,0,0,.63,2.29,2.16,2.16,0,0,0,3.43,0,4.1,4.1,0,0,0,.63-2.45,3.42,3.42,0,0,0-.63-2.16,2.07,2.07,0,0,0-1.73-.81,2,2,0,0,0-1.7.8A3.76,3.76,0,0,0,739.44,7.45Z"
                                [ngStyle]="config.inactiveLabel"
                            />
                            <svg:path
                                d="M904.09,5.47h3.19V6.84h-3.19v3.62h-1.46V6.84h-3.19V5.47h3.19V2.12h1.46Z"
                                [ngStyle]="config.inactiveLabel"
                            />
                            <svg:path
                                d="M916.41,11.61h-7.5v-1l4-4.4a9.45,9.45,0,0,0,1.22-1.62,2.71,2.71,0,0,0,.33-1.29,2.05,2.05,0,0,0-.54-1.47,1.9,1.9,0,0,0-1.45-.57,2.25,2.25,0,0,0-1.69.62,2.36,2.36,0,0,0-.6,1.72h-1.46a3.38,3.38,0,0,1,1-2.56,3.78,3.78,0,0,1,2.73-1A3.63,3.63,0,0,1,915,.84a2.86,2.86,0,0,1,.93,2.23q0,1.69-2.16,4l-3.07,3.33h5.75Z"
                                [ngStyle]="config.inactiveLabel"
                            />
                            <svg:path
                                d="M926,4.3h-2.23a3.87,3.87,0,0,1,1.57,3.31v.13a4.33,4.33,0,0,1-.48,2,3.65,3.65,0,0,1-3.32,2,3.49,3.49,0,0,1-2.76-1.19,4.68,4.68,0,0,1-1-3.2v-.1a4.83,4.83,0,0,1,.46-2.15,3.54,3.54,0,0,1,1.32-1.49,3.66,3.66,0,0,1,2-.54H926Zm-6.8,3.15a3.74,3.74,0,0,0,.63,2.29,2.16,2.16,0,0,0,3.43,0,4.1,4.1,0,0,0,.63-2.45,3.42,3.42,0,0,0-.63-2.16,2.07,2.07,0,0,0-1.73-.81,2,2,0,0,0-1.7.8A3.76,3.76,0,0,0,919.21,7.45Z"
                                [ngStyle]="config.inactiveLabel"
                            />
                            <svg:path
                                d="M1074.37,5.47h3.19V6.84h-3.19v3.62h-1.46V6.84h-3.19V5.47h3.19V2.12h1.46Z"
                                [ngStyle]="config.inactiveLabel"
                            />
                            <svg:path
                                d="M1081.3,5.18h1.09a2.4,2.4,0,0,0,1.62-.54,1.81,1.81,0,0,0,.59-1.42,1.78,1.78,0,0,0-2-2,2.09,2.09,0,0,0-1.51.54,1.89,1.89,0,0,0-.56,1.43h-1.46a2.92,2.92,0,0,1,1-2.26,3.62,3.62,0,0,1,2.53-.9,3.58,3.58,0,0,1,2.54.86,3.1,3.1,0,0,1,.92,2.38,2.51,2.51,0,0,1-.48,1.45,2.92,2.92,0,0,1-1.32,1,2.81,2.81,0,0,1,1.46,1,2.75,2.75,0,0,1,.52,1.69,3.13,3.13,0,0,1-1,2.45,3.77,3.77,0,0,1-2.62.9,3.87,3.87,0,0,1-2.62-.87,2.9,2.9,0,0,1-1-2.3h1.46A1.87,1.87,0,0,0,1081,10a2.24,2.24,0,0,0,1.58.54,2.2,2.2,0,0,0,1.61-.55,2.12,2.12,0,0,0,.56-1.58,1.93,1.93,0,0,0-.61-1.53,2.67,2.67,0,0,0-1.77-.55h-1.09Z"
                                [ngStyle]="config.inactiveLabel"
                            />
                            <svg:path
                                d="M1096.3,4.3h-2.23a3.87,3.87,0,0,1,1.57,3.31v.13a4.33,4.33,0,0,1-.48,2,3.65,3.65,0,0,1-3.32,2,3.49,3.49,0,0,1-2.76-1.19,4.68,4.68,0,0,1-1-3.2v-.1a4.83,4.83,0,0,1,.46-2.15,3.54,3.54,0,0,1,1.32-1.49,3.66,3.66,0,0,1,2-.54h4.53Zm-6.8,3.15a3.74,3.74,0,0,0,.63,2.29,2.16,2.16,0,0,0,3.43,0,4.1,4.1,0,0,0,.63-2.45,3.42,3.42,0,0,0-.63-2.16,2.07,2.07,0,0,0-1.73-.81,2,2,0,0,0-1.7.8A3.76,3.76,0,0,0,1089.49,7.45Z"
                                [ngStyle]="config.inactiveLabel"
                            />
                        </svg:g>

                        @if (showCopyright) {
                            <svg:text
                                [attr.x]="viewBox.width"
                                y="242"
                                font-size="12"
                                text-anchor="end"
                            >
                                © 2023 Predictive Index, LLC
                            </svg:text>
                        }
                    </svg:g>

                    @if (ranges) {
                        <svg:g id="FactorRanges">
                            <!-- rectangles -->
                            <svg:g
                                [behavioralRangesRectangles]="ranges"
                                [dragMap]="dragMap"
                                [highlighted]="highlightRanges"
                                [useRangeRectThickStyle]="showAsThumbnail"
                            ></svg:g>
                            <!-- draggable parts of rectangles -->
                            @for (
                                dimensionData of factorRanges;
                                track trackBySVGDimensionData(i, dimensionData);
                                let i = $index
                            ) {
                                <svg:g>
                                    @if (
                                        validInputRanges() && draggableRanges
                                    ) {
                                        <svg:g>
                                            @for (
                                                dragLine of getDragLinesData(
                                                    dimensionData.factor,
                                                    dimensionData.rectY
                                                );
                                                track trackBySVGDragline(
                                                    i,
                                                    dragLine
                                                );
                                                let i = $index
                                            ) {
                                                <svg:line
                                                    [attr.x1]="
                                                        dragLine.triangleRight
                                                    "
                                                    [attr.x2]="
                                                        dragLine.triangleRight
                                                    "
                                                    [attr.y1]="
                                                        dragLine.triangleTop
                                                    "
                                                    [attr.y2]="
                                                        dragLine.triangleBottom
                                                    "
                                                    [attr.stroke]="
                                                        config.gradientColors[1]
                                                    "
                                                    strokeWidth="1.5"
                                                />
                                            }
                                            <svg:g>
                                                <svg:rect
                                                    (mousedown)="
                                                        dimensionData.leftRectMousedown()
                                                    "
                                                    [attr.x]="
                                                        this.getDraggedRectX(
                                                            dimensionData.factor
                                                        ) -
                                                        config.draggableRectWidth /
                                                            2
                                                    "
                                                    [attr.y]="
                                                        dimensionData.rectY
                                                    "
                                                    [attr.width]="
                                                        config.draggableRectWidth
                                                    "
                                                    [attr.height]="
                                                        config.rectHeight
                                                    "
                                                    [ngStyle]="
                                                        config.draggableRectStyle
                                                    "
                                                    piTesting="behavioral-ranges-left-draggable"
                                                />
                                                <svg:rect
                                                    (mousedown)="
                                                        dimensionData.rightRectMousedown()
                                                    "
                                                    [attr.x]="
                                                        this.getDraggedRectX(
                                                            dimensionData.factor,
                                                            true
                                                        ) -
                                                        config.draggableRectWidth /
                                                            2
                                                    "
                                                    [attr.y]="
                                                        dimensionData.rectY
                                                    "
                                                    [attr.width]="
                                                        config.draggableRectWidth
                                                    "
                                                    [attr.height]="
                                                        config.rectHeight
                                                    "
                                                    [ngStyle]="
                                                        config.draggableRectStyle
                                                    "
                                                    piTesting="behavioral-ranges-right-draggable"
                                                />
                                            </svg:g>
                                        </svg:g>
                                    }
                                    <svg:g>
                                        @for (
                                            lineData of getHoveredPatternRangeLinesData();
                                            track lineData
                                        ) {
                                            <svg:g>
                                                <svg:line
                                                    [attr.x1]="lineData.x"
                                                    [attr.x2]="lineData.x"
                                                    [attr.y1]="lineData.y - 10"
                                                    [attr.y2]="lineData.y + 10"
                                                    strokeWidth="1.5"
                                                    stroke="black"
                                                />
                                                <svg:line
                                                    [attr.x1]="lineData.x"
                                                    [attr.x2]="
                                                        lineData.x +
                                                        lineData.lineWidth
                                                    "
                                                    [attr.y1]="lineData.y"
                                                    [attr.y2]="lineData.y"
                                                    strokeWidth="1.5"
                                                    stroke="black"
                                                />
                                                <svg:text
                                                    [attr.x]="
                                                        lineData.x +
                                                        1.35 *
                                                            lineData.lineWidth
                                                    "
                                                    [attr.y]="lineData.y + 5"
                                                    fontSize="20"
                                                >
                                                    {{ lineData.factorKey }}
                                                </svg:text>
                                                <svg:line
                                                    [attr.x1]="
                                                        lineData.x +
                                                        2 * lineData.lineWidth
                                                    "
                                                    [attr.x2]="
                                                        lineData.x +
                                                        3 * lineData.lineWidth
                                                    "
                                                    [attr.y1]="lineData.y"
                                                    [attr.y2]="lineData.y"
                                                    strokeWidth="1.5"
                                                    stroke="black"
                                                />
                                                <svg:line
                                                    [attr.x1]="
                                                        lineData.x +
                                                        3 * lineData.lineWidth
                                                    "
                                                    [attr.x2]="
                                                        lineData.x +
                                                        3 * lineData.lineWidth
                                                    "
                                                    [attr.y1]="lineData.y - 10"
                                                    [attr.y2]="lineData.y + 10"
                                                    strokeWidth="1.5"
                                                    stroke="black"
                                                />
                                            </svg:g>
                                        }
                                    </svg:g>
                                </svg:g>
                            }
                        </svg:g>
                    }
                </svg:g>
                @if (hoveredPattern) {
                    <svg:g
                        id="HoveredPattern"
                        [singleBehavioralPattern]="hoveredPattern"
                        [options]="{
                            showTitle: false,
                            isOverlay: true
                        }"
                    ></svg:g>
                }
            </svg>
        </div>
    `,
    standalone: false
})
export class BehavioralRangesComponent
    implements OnInit, AfterContentInit, OnDestroy, OnChanges
{
    @Input()
    hasBeenHovered: boolean;
    @Input()
    ranges: BehavioralFactorRanges;
    @Input()
    hoveredPatternRanges: BehavioralFactorRanges;
    @Input()
    draggableRanges: boolean;
    @Input()
    showCopyright = false;
    @Input()
    hoveredPattern: Pattern;
    @Input()
    highlightRanges = false;
    @Input()
    showAsThumbnail = false;
    @Input()
    reportTexts: Map<string, string>;

    @Output()
    readonly rangesUpdated = new EventEmitter<BehavioralFactorRanges>();

    config = config;

    viewBox = {
        width: 1100,
        height: 225
    };

    factors = BehavioralRangesUtil.factors;

    // State
    dragMap: DragStartEndMap;
    dragFactor: BehavioralFactor;
    dragging = false;
    draggingRight = false;
    svg: SVGElement;
    point: SVGPoint;
    SVG: any;

    factorRanges: FactorRange[];

    private mouseup$: Subscription;
    private mousemove$: Subscription;
    private outputDebouncer$ = new Subject<BehavioralFactorRanges>();

    private readonly mouseMoveResponseTimeMs = 5;
    private readonly outputEventDebounceTimeMs = 250;

    constructor(
        public TR: TranslationService,
        private elRef: ElementRef,
        private util: Util
    ) {
        this.SVG = this.util.svg;
        // emits only the latest event after a period
        this.outputDebouncer$
            .pipe(debounceTime(this.outputEventDebounceTimeMs))
            .subscribe((event) => {
                this.rangesUpdated.emit(event);
            });
    }

    ngOnInit() {
        // https://stackoverflow.com/questions/46389002/how-to-listen-for-mousemove-event-on-document-object-in-angular

        // Initialize draggable listeners
        if (this.draggableRanges) {
            this.mouseup$ = fromEvent(document.body, 'mouseup').subscribe(
                (e) => {
                    if (this.dragging) {
                        this.dragging = false;
                    }
                }
            );

            this.mousemove$ = fromEvent(document.body, 'mousemove')
                .pipe(debounceTime(this.mouseMoveResponseTimeMs))
                .subscribe((e) => {
                    if (this.dragging) {
                        e.preventDefault();
                        const x = BehavioralRangesUtil.getBoundedValue(
                            this.dragMap,
                            this.dragFactor,
                            this.draggingRight,
                            this.cursorPoint(e as MouseEvent).x
                        );
                        const startEnd = this.draggingRight ? 'end' : 'start';
                        this.dragMap[this.dragFactor][startEnd] = x;
                        this.updateRanges();
                    }
                });
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        // Recompute these when the input ranges change
        if (
            changes.ranges &&
            changes.ranges.currentValue &&
            !isEqual(changes.ranges.currentValue, changes.ranges.previousValue)
        ) {
            this.dragMap = BehavioralRangesUtil.getInitialDragMap(
                this.factors,
                this.ranges
            );

            this.factorRanges = this.getFactorRangesDimensionsData();
        }
    }

    ngAfterContentInit() {
        this.svg = this.elRef.nativeElement.querySelector('svg') as SVGElement;
        this.point = (this.svg as any).createSVGPoint();
        if (this.draggableRanges && this.dragMap) {
            this.updateRanges(false);
        }
    }

    ngOnDestroy() {
        this.outputDebouncer$.unsubscribe();
        if (this.draggableRanges) {
            this.mouseup$.unsubscribe();
            this.mousemove$.unsubscribe();
        }
    }

    getViewboxDimensions(): string {
        return `0 0 ${this.viewBox.width} ${this.viewBox.height}`;
    }

    /**
     * Highlight a factor's descriptor if that factor passes the mid point going
     * in that direction (positive or negative)
     *
     * @param factor
     * @param right
     */
    shouldHighlightFactor(factor: BehavioralFactor, right = false): boolean {
        if (!this.ranges) {
            return false;
        }
        const ranges = this.ranges[factor];
        return right ? ranges.max > 0 : ranges.min < 0;
    }

    validInputRanges(): boolean {
        return BehavioralRangesUtil.validInputRanges(this.ranges);
    }

    /**
     * Get all the relevant data to draw the factor ranges.
     */
    getFactorRangesDimensionsData(): FactorRange[] {
        return BehavioralRangesUtil.getFactorRangesDimensionsData(
            this.factors,
            this.dragMap,
            this.draggableRanges,
            this.ranges,
            this.draggableOnMousedown.bind(this)
        );
    }

    getDraggedRectX(factor: BehavioralFactor, isRight = false): number {
        return BehavioralRangesUtil.getDraggedRectX(
            this.draggableRanges,
            this.dragMap,
            this.ranges,
            factor,
            isRight
        );
    }

    getHoveredPatternRangeLinesData(): {
        minMax: MinMax;
        x: number;
        y: number;
        width: number;
        lineWidth: number;
        factorKey: BehavioralFactor;
    }[] {
        return BehavioralRangesUtil.getHoveredPatternRangeLinesData(
            this.hoveredPatternRanges,
            this.factors
        );
    }

    /**
     * Get the position data for drawing the lines on either end of the
     * draggable rect.
     *
     * @param factor
     * @param rectY
     * @param rectWidth
     */
    getDragLinesData(
        factor: BehavioralFactor,
        rectY: number
    ): {
        triangleRight: number;
        triangleTop: number;
        triangleBottom: number;
    }[] {
        const draggedRectX = this.getDraggedRectX(factor, false);
        const draggedRectXRight = this.getDraggedRectX(factor, true);
        return [
            // Left two
            this.getDragLine(draggedRectX, 6, rectY),
            this.getDragLine(draggedRectX, 12, rectY),
            // Right two
            this.getDragLine(draggedRectXRight, -6, rectY),
            this.getDragLine(draggedRectXRight, -12, rectY)
        ];
    }

    /**
     * Callback for when range is dragged.
     *
     * @param factor
     * @param isRight
     */
    draggableOnMousedown(
        factor: BehavioralFactor,
        isRight = false
    ): () => void {
        return () => {
            this.dragging = true;
            this.draggingRight = isRight;
            this.dragFactor = factor;
        };
    }

    getFactorLabel(factorKey: string) {
        if (this.showAsThumbnail) {
            return '';
        }

        return this.getTranslationValue(factorKey);
    }

    getTranslationValue(trKey: string) {
        const overrideText = this.reportTexts ? this.reportTexts[trKey] : null;
        return overrideText ?? this.TR.key(trKey);
    }

    trackBySVGDimensionData(i: number, dimensionData: FactorRange): number {
        return dimensionData.rectWidth + dimensionData.rectY;
    }

    trackBySVGDragline(i: number, dragLine: any): any {
        return (
            dragLine.triangleRight +
            dragLine.triangleTop +
            dragLine.triangleBottom
        );
    }

    /**
     * Take the svg drag position data and convert it to the actual sigma ranges
     * for the pattern.
     */
    private updateRanges(fireUpdate = true) {
        const dragMapA = this.dragMap.A;
        const dragMapB = this.dragMap.B;
        const dragMapC = this.dragMap.C;
        const dragMapD = this.dragMap.D;
        const ranges = {
            A: BehavioralRangesUtil.getRangeFromRect(
                dragMapA.start,
                dragMapA.end
            ),
            B: BehavioralRangesUtil.getRangeFromRect(
                dragMapB.start,
                dragMapB.end
            ),
            C: BehavioralRangesUtil.getRangeFromRect(
                dragMapC.start,
                dragMapC.end
            ),
            D: BehavioralRangesUtil.getRangeFromRect(
                dragMapD.start,
                dragMapD.end
            )
        };

        if (fireUpdate) {
            if (ranges != null) {
                this.outputDebouncer$.next(ranges);
            }
        }
    }

    /**
     * Get point of the mouse relative to the SVG space
     */
    private cursorPoint(evt: MouseEvent): SVGPoint {
        this.point.x = evt.clientX;
        this.point.y = evt.clientY;
        return this.point.matrixTransform(
            (this.svg as any).getScreenCTM().inverse()
        );
    }

    /**
     * Get the relevant data to draw one of the lines at the ends of the
     * draggable rects.
     *
     * @param startX
     * @param offsetX
     * @param rectY
     * @param rectWidth
     * @param isRight Whether it's the left or right line. There are two on each
     * side of the rect. Left or right refers to the left or right of the pair.
     */
    private getDragLine(startX: number, offsetX: number, rectY: number) {
        const triangleWidth = 14;

        const triangleRight = startX + offsetX;
        const triangleMiddle = rectY + this.config.rectHeight / 2;
        const triangleTop = triangleMiddle - triangleWidth;
        const triangleBottom = triangleMiddle + triangleWidth;

        return {
            triangleRight,
            triangleTop,
            triangleBottom
        };
    }
}

interface FactorRange {
    rectStartEnd: { start: number; end: number };
    minMax: MinMax;
    rectY: number;
    rectWidth: number;
    factor: BehavioralFactor;
    leftRectMousedown: () => void;
    rightRectMousedown: () => void;
}
